// Local translations
export const T = {
  en_GB: {
    aboutSso: "About Single Sign-On",
    aboutSsoDescription:
      "SSO allows users to sign in using your organization's identity provider (IdP)",
    aboutSsoDetails:
      "When SSO is enabled, users can sign in with their corporate credentials through your identity provider (Microsoft Entra ID, Okta, Google Workspace, etc.). Normal username/password authentication remains available for admin accounts.",
    callbackUrlLabel: "Callback URL for your IdP:",
    providerIdLabel: "Provider ID:",
    providerIdNote:
      "Register this callback URL as a Web redirect URI in your identity provider before saving the settings below.",
    ssoConfiguration: "SSO Configuration",
    configureOidc: "Configure OpenID Connect (OIDC) settings",
    enableSso: "Enable SSO",
    enableSsoDescription: "Allow users to sign in with your identity provider",
    providerName: "Provider Name",
    providerNamePlaceholder: "e.g., Microsoft, Okta, Google",
    providerNameHint: "Display name shown on the login button",
    issuerUrl: "Issuer URL",
    issuerUrlPlaceholder: "https://login.microsoftonline.com/<tenant-id>/v2.0",
    issuerUrlHint:
      "The issuer URL from your identity provider's OIDC configuration. Use your tenant-specific URL, not common or organizations.",
    issuerDiffersTitle: "The identity provider reports a different issuer",
    issuerDiffersBody: (entered: string, issuer: string) =>
      `You entered ${entered}. The identity provider reports its issuer as ${issuer}. Sign-ins are verified against the reported value, so that is what gets stored once you confirm.`,
    issuerDiffersConfirm: (issuer: string) => `Use ${issuer} as the issuer`,
    issuerDiffersNeedsConfirm:
      "Confirm the issuer reported by the identity provider before saving.",
    savedWithIssuer: (issuer: string) => `Saved. Issuer stored as ${issuer}`,
    connectionFoundDiffers: "OpenID configuration found, issuer differs",
    clientId: "Client ID",
    clientIdPlaceholder: "your-client-id",
    clientSecret: "Client Secret",
    clientSecretPlaceholder: "Enter client secret",
    clientSecretExisting: "Leave blank to keep the existing secret",
    removeClientSecret: "Remove client secret",
    removeClientSecretTitle: "Remove the client secret?",
    removeClientSecretDescription:
      "The stored client secret is deleted. Only do this if the identity provider client no longer uses a secret (a public client with PKCE), otherwise sign-in will stop working.",
    removeClientSecretConfirm: "Remove",
    cancel: "Cancel",
    secretRemoved: "Client secret removed",
    clientSecretReenter:
      "The issuer URL or client ID has changed, so re-enter the client secret to save",
    clientSecretRequired: "Only needed if the identity provider client is confidential",
    testConnection: "Check issuer URL",
    testing: "Checking...",
    enterIssuerFirst: "Please enter an Issuer URL first",
    connectionSuccessful: "Issuer URL found",
    testSuccessMessage:
      "The identity provider's OpenID configuration was found at this issuer URL. Save the settings, then use Test sign-in to check the whole sign-in.",
    issuer: "Issuer:",
    authorization: "Authorization:",
    token: "Token:",
    save: "Save",
    savedSuccessfully: "Saved",
    failedToSave: "Failed to save settings",
    connectionTestFailed: "Could not check the issuer URL",
    pendingTitle: "Confirm the new single sign-on address",
    pendingBody: (date: string) =>
      `Your organization's address has changed. Single sign-on uses the old callback URL until ${date}, then switches to the new one automatically — sign-in stops working if the identity provider does not have it by then. Add it and confirm before that date.`,
    pendingBodyNoDate:
      "Your organization's address has changed. Single sign-on still uses the old callback URL. Add the new one and confirm the change, otherwise single sign-on may stop working.",
    pendingBodyOverdue: (date: string) =>
      `Your organization's address has changed and the grace period ended on ${date}. Single sign-on is switching to the new callback URL — add it in the identity provider now.`,
    pendingStep1:
      "In your identity provider, add this redirect URI to the application used for single sign-on:",
    pendingStep2:
      "Confirm below. Sign-ins then use the new URL, and the old redirect URI can be removed from the identity provider.",
    confirmChange: "I have updated the identity provider — confirm",
    changeConfirmed: "The new single sign-on address is now in use",
    confirmFailed: "Failed to confirm the change",
    inUseUntilConfirmed: "In use until the address change above is confirmed.",
    testCallback: "Test sign-in with the new address",
    testCallbackHint:
      "Optional: you are sent to the identity provider and back here, nothing changes. If the new redirect URI is missing, the identity provider shows its own error page instead of sending you back.",
    testStartFailed: "Could not start the test sign-in",
    testSucceededTitle: "The identity provider accepts the new address",
    testSucceeded: "Signing in through the new callback URL works. You can confirm the change.",
    testFailedTitle: "The test sign-in failed",
    testFailedCancelled: "The sign-in was cancelled or denied by the identity provider.",
    testFailedExpired: "The test took too long. Start it again.",
    testFailedSecretUndecryptable:
      "The stored client secret can't be decrypted. Re-enter the client secret, save, and test again.",
    testFailedEndpointNotAllowed:
      "A saved identity provider endpoint is not allowed (it must be a public https URL). Save the issuer URL again to refresh the endpoints, then test again.",
    testFailedRequest:
      "QP could not reach the identity provider's token endpoint. Check that it is reachable, then test again.",
    testFailedGeneric: (reason: string) =>
      `The identity provider returned “${reason}”. Check the redirect URI and client settings, then test again.`,
    testFailedIdpError:
      "The identity provider returned an error. Check the redirect URI and client settings, then test again.",
    testFailedNotConfigured:
      "The saved settings are incomplete (client ID or token endpoint missing). Save the issuer URL again, then test again.",
    testFailedWrongCallback:
      "The sign-in came back to a different callback URL than the one being tested. Start the test again.",
    testFailedNoCode:
      "The identity provider did not send a sign-in code back. Check the redirect URI and client settings, then test again.",
    testFailedUnknown: "The test sign-in failed for an unknown reason. Start it again.",
    testSignIn: "Test sign-in",
    testSignInHint:
      "Signs in through your identity provider with the saved settings and sends you back here. No users, links or sessions change, and it also works while you are signed in with a password.",
    saveBeforeTest: "Save the settings before testing them.",
    testCurrentSucceededTitle: "Sign-in through the identity provider works",
    testCurrentSucceeded:
      "The identity provider accepted the sign-in and the callback URL. Nothing was changed.",
    idpMessageLabel: "Message from the identity provider:",
    idpMessageUnverifiedLabel: "Description in the sign-in response (not verified):",
    idpMessageUnverifiedHint:
      "This text came through the signing-in browser. Anyone who starts a sign-in can write it, so don't follow links or instructions in it.",
    idpMessageNone: "(the identity provider sent no description)",
    referenceLabel: "Reference",
    errorCodeLabel: "Code",
    lookupTitle: "Look up a failed sign-in",
    lookupHint:
      "A user who could not sign in sees a reference on the login page. Paste it here to see what the identity provider reported.",
    lookupPlaceholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
    lookupButton: "Look up",
    lookupInvalidRef: "That is not a sign-in reference.",
    lookupNotFound:
      "No failed sign-in is recorded for this reference (entries are kept for 7 days).",
    lookupFailed: "Could not look up the reference.",
  },
  nb_NO: {
    aboutSso: "Om enkel innlogging (SSO)",
    aboutSsoDescription: "SSO lar brukere logge inn med organisasjonens identitetsleverandør (IdP)",
    aboutSsoDetails:
      "Når SSO er aktivert, kan brukere logge inn med sin bedriftskonto gjennom identitetsleverandøren (Microsoft Entra ID, Okta, Google Workspace, osv.). Vanlig brukernavn/passord-autentisering er fortsatt tilgjengelig for adminkontoer.",
    callbackUrlLabel: "Callback-URL for din IdP:",
    providerIdLabel: "Leverandør-ID:",
    providerIdNote:
      "Registrer denne callback-URLen som Web-redirect-URI hos identitetsleverandøren før du lagrer innstillingene under.",
    ssoConfiguration: "SSO-konfigurasjon",
    configureOidc: "Konfigurer OpenID Connect (OIDC)-innstillinger",
    enableSso: "Aktiver SSO",
    enableSsoDescription: "Tillat brukere å logge inn med din identitetsleverandør",
    providerName: "Leverandørnavn",
    providerNamePlaceholder: "f.eks. Microsoft, Okta, Google",
    providerNameHint: "Visningsnavn som vises på innloggingsknappen",
    issuerUrl: "Utsteder-URL",
    issuerUrlPlaceholder: "https://login.microsoftonline.com/<tenant-id>/v2.0",
    issuerUrlHint:
      "Utsteder-URLen fra identitetsleverandørens OIDC-konfigurasjon. Bruk den leietakerspesifikke URL-en, ikke common eller organizations.",
    issuerDiffersTitle: "Identitetsleverandøren oppgir en annen utsteder",
    issuerDiffersBody: (entered: string, issuer: string) =>
      `Du skrev inn ${entered}. Identitetsleverandøren oppgir utstederen som ${issuer}. Innlogginger verifiseres mot den oppgitte verdien, så det er den som lagres når du bekrefter.`,
    issuerDiffersConfirm: (issuer: string) => `Bruk ${issuer} som utsteder`,
    issuerDiffersNeedsConfirm: "Bekreft utstederen identitetsleverandøren oppgir før du lagrer.",
    savedWithIssuer: (issuer: string) => `Lagret. Utsteder lagret som ${issuer}`,
    connectionFoundDiffers: "OpenID-konfigurasjon funnet, utstederen er en annen",
    clientId: "Klient-ID",
    clientIdPlaceholder: "din-klient-id",
    clientSecret: "Klienthemmelighet",
    clientSecretPlaceholder: "Skriv inn klienthemmelighet",
    clientSecretExisting: "La stå tom for å beholde eksisterende hemmelighet",
    removeClientSecret: "Fjern klienthemmelighet",
    removeClientSecretTitle: "Fjerne klienthemmeligheten?",
    removeClientSecretDescription:
      "Den lagrede klienthemmeligheten slettes. Gjør dette bare hvis klienten hos identitetsleverandøren ikke lenger bruker hemmelighet (offentlig klient med PKCE), ellers slutter innloggingen å virke.",
    removeClientSecretConfirm: "Fjern",
    cancel: "Avbryt",
    secretRemoved: "Klienthemmelighet fjernet",
    clientSecretReenter:
      "Utsteder-URL eller klient-ID er endret, så skriv inn klienthemmeligheten på nytt for å lagre",
    clientSecretRequired: "Kun nødvendig hvis klienten hos identitetsleverandøren er konfidensiell",
    testConnection: "Sjekk utsteder-URL",
    testing: "Sjekker...",
    enterIssuerFirst: "Vennligst skriv inn en utsteder-URL først",
    connectionSuccessful: "Utsteder-URL funnet",
    testSuccessMessage:
      "Identitetsleverandørens OpenID-konfigurasjon ble funnet på denne utsteder-URLen. Lagre innstillingene, og bruk Test innlogging for å sjekke hele innloggingen.",
    issuer: "Utsteder:",
    authorization: "Autorisering:",
    token: "Token:",
    save: "Lagre",
    savedSuccessfully: "Lagret",
    failedToSave: "Kunne ikke lagre innstillinger",
    connectionTestFailed: "Kunne ikke sjekke utsteder-URLen",
    pendingTitle: "Bekreft ny påloggingsadresse for enkel pålogging",
    pendingBody: (date: string) =>
      `Organisasjonens adresse er endret. Enkel pålogging bruker den gamle callback-URLen til ${date}, og bytter deretter automatisk til den nye — innloggingen slutter å virke hvis identitetsleverandøren ikke har den innen da. Legg den inn og bekreft før fristen.`,
    pendingBodyNoDate:
      "Organisasjonens adresse er endret. Enkel pålogging bruker fortsatt den gamle callback-URLen. Legg inn den nye og bekreft endringen, ellers kan enkel pålogging slutte å virke.",
    pendingBodyOverdue: (date: string) =>
      `Organisasjonens adresse er endret, og fristen gikk ut ${date}. Enkel pålogging bytter til den nye callback-URLen — legg den inn hos identitetsleverandøren nå.`,
    pendingStep1:
      "Legg til denne redirect-URIen i applikasjonen som brukes til enkel pålogging hos identitetsleverandøren:",
    pendingStep2:
      "Bekreft under. Innlogging bruker da den nye URLen, og den gamle redirect-URIen kan fjernes hos identitetsleverandøren.",
    confirmChange: "Jeg har oppdatert identitetsleverandøren — bekreft",
    changeConfirmed: "Den nye påloggingsadressen er tatt i bruk",
    confirmFailed: "Kunne ikke bekrefte endringen",
    inUseUntilConfirmed: "Brukes til adresseendringen over er bekreftet.",
    testCallback: "Test innlogging med den nye adressen",
    testCallbackHint:
      "Valgfritt: du sendes til identitetsleverandøren og tilbake hit, ingenting endres. Mangler den nye redirect-URIen, viser identitetsleverandøren sin egen feilside i stedet for å sende deg tilbake.",
    testStartFailed: "Kunne ikke starte test-innloggingen",
    testSucceededTitle: "Identitetsleverandøren godtar den nye adressen",
    testSucceeded: "Innlogging via den nye callback-URLen virker. Du kan bekrefte endringen.",
    testFailedTitle: "Test-innloggingen mislyktes",
    testFailedCancelled: "Innloggingen ble avbrutt eller avvist av identitetsleverandøren.",
    testFailedExpired: "Testen tok for lang tid. Start den på nytt.",
    testFailedSecretUndecryptable:
      "Den lagrede klienthemmeligheten kan ikke dekrypteres. Skriv inn klienthemmeligheten på nytt, lagre og test på nytt.",
    testFailedEndpointNotAllowed:
      "Et lagret endepunkt hos identitetsleverandøren er ikke tillatt (det må være en offentlig https-URL). Lagre issuer-URLen på nytt for å oppdatere endepunktene, og test på nytt.",
    testFailedRequest:
      "QP kunne ikke nå token-endepunktet hos identitetsleverandøren. Sjekk at det er tilgjengelig, og test på nytt.",
    testFailedGeneric: (reason: string) =>
      `Identitetsleverandøren svarte «${reason}». Sjekk redirect-URIen og klientinnstillingene, og test på nytt.`,
    testFailedIdpError:
      "Identitetsleverandøren svarte med en feil. Sjekk redirect-URIen og klientinnstillingene, og test på nytt.",
    testFailedNotConfigured:
      "De lagrede innstillingene er ufullstendige (klient-ID eller token-endepunkt mangler). Lagre issuer-URLen på nytt, og test på nytt.",
    testFailedWrongCallback:
      "Innloggingen kom tilbake til en annen callback-URL enn den som ble testet. Start testen på nytt.",
    testFailedNoCode:
      "Identitetsleverandøren sendte ikke tilbake en innloggingskode. Sjekk redirect-URIen og klientinnstillingene, og test på nytt.",
    testFailedUnknown: "Test-innloggingen mislyktes av en ukjent årsak. Start den på nytt.",
    testSignIn: "Test innlogging",
    testSignInHint:
      "Logger inn via identitetsleverandøren med de lagrede innstillingene og sender deg tilbake hit. Ingen brukere, koblinger eller sessions endres, og det virker også når du er logget inn med passord.",
    saveBeforeTest: "Lagre innstillingene før du tester dem.",
    testCurrentSucceededTitle: "Innlogging via identitetsleverandøren virker",
    testCurrentSucceeded:
      "Identitetsleverandøren godtok innloggingen og callback-URLen. Ingenting ble endret.",
    idpMessageLabel: "Melding fra identitetsleverandøren:",
    idpMessageUnverifiedLabel: "Beskrivelse i innloggingssvaret (ikke verifisert):",
    idpMessageUnverifiedHint:
      "Denne teksten kom via nettleseren som logget inn. Alle som starter en innlogging, kan skrive den, så ikke følg lenker eller instruksjoner i den.",
    idpMessageNone: "(identitetsleverandøren sendte ingen beskrivelse)",
    referenceLabel: "Referanse",
    errorCodeLabel: "Kode",
    lookupTitle: "Slå opp en mislykket innlogging",
    lookupHint:
      "En bruker som ikke kunne logge inn, ser en referanse på innloggingssiden. Lim den inn her for å se hva identitetsleverandøren rapporterte.",
    lookupPlaceholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
    lookupButton: "Slå opp",
    lookupInvalidRef: "Dette er ikke en innloggingsreferanse.",
    lookupNotFound:
      "Ingen mislykket innlogging er registrert for denne referansen (oppføringer beholdes i 7 dager).",
    lookupFailed: "Kunne ikke slå opp referansen.",
  },
} as const;
